import React, {useState, useEffect} from 'react'
import {useNavigate} from 'react-router-dom'
import {
  getDuplicateProfiles,
  DuplicateGroup,
  DuplicateProfile,
  DuplicateSummary,
} from './core/_requests'

const colors = {
  bgSecondary: '#ffffff',
  bgTertiary: '#e8ecef',
  bgHover: '#e1e5ea',
  textPrimary: '#1e1e2d',
  textSecondary: '#5e6278',
  textMuted: '#a1a5b7',
  accentPrimary: '#3699ff',
  accentSuccess: '#1bc5bd',
  accentWarning: '#ffa800',
  accentDanger: '#f64e60',
  borderColor: 'rgba(0, 0, 0, 0.08)',
}

const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString('he-IL') : '—')

// The stored profile_picture_url expires, so fall back to Graph, then to initials.
const FB_ACCESS_TOKEN = process.env.REACT_APP_FB_ACCESS_TOKEN || ''
const avatarUrl = (p: {profile_picture_url?: string; facebook_id?: string; name?: string}) => {
  if (p.profile_picture_url) return p.profile_picture_url
  if (p.facebook_id) {
    return `https://graph.facebook.com/${p.facebook_id}/picture?type=square${
      FB_ACCESS_TOKEN ? `&access_token=${FB_ACCESS_TOKEN}` : ''
    }`
  }
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(p.name || '?')}&background=random&color=fff&size=40`
}

// Shared by the column headers and the rows so they can't drift apart.
const PROFILE_COLUMNS = '1.8fr 1.6fr 1.3fr 1.5fr 0.9fr 1.5fr'

const Badge: React.FC<{text: string; color: string}> = ({text, color}) => (
  <span
    style={{
      padding: '2px 8px',
      borderRadius: '6px',
      background: `${color}22`,
      color,
      fontSize: '11px',
      fontWeight: 600,
      whiteSpace: 'nowrap',
    }}
  >
    {text}
  </span>
)

const DuplicateProfilesPage: React.FC = () => {
  const navigate = useNavigate()
  const [summary, setSummary] = useState<DuplicateSummary | null>(null)
  const [groups, setGroups] = useState<DuplicateGroup[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const res = await getDuplicateProfiles()
        if (!cancelled) {
          setSummary(res.data.summary)
          setGroups(res.data.groups || [])
          setError(null)
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.response?.data?.error || e?.message || 'שגיאה בטעינה')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  const filtered = groups.filter((g) => {
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    // Every account in the group, not just the first — a split group is findable
    // by any of the customers sharing the profile.
    return (
      g.accounts.some(
        (a) =>
          a.account_name.toLowerCase().includes(q) ||
          a.account_company.toLowerCase().includes(q) ||
          a.account_email.toLowerCase().includes(q)
      ) ||
      (g.match_value || '').toLowerCase().includes(q) ||
      g.profiles.some((p) => p.name.toLowerCase().includes(q) || p.facebook_username.toLowerCase().includes(q))
    )
  })

  const statsData = [
    {
      label: 'קבוצות כפילות',
      value: summary?.duplicate_groups || 0,
      icon: 'bi-files',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    {
      label: 'משותפים בין חשבונות',
      value: summary?.cross_account_groups || 0,
      icon: 'bi-arrow-left-right',
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    },
    {
      label: 'פרופילים מיותרים',
      value: summary?.extra_profiles || 0,
      icon: 'bi-trash3-fill',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    },
    {
      label: 'חשבונות מושפעים',
      value: summary?.accounts_affected || 0,
      icon: 'bi-people-fill',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    },
    {
      label: 'חשבונות בתקופת ניסיון',
      value: summary?.active_trial_accounts || 0,
      icon: 'bi-hourglass-split',
      gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    },
  ]

  const accountLabel = (a: {account_name: string; account_company: string}) =>
    [a.account_name, a.account_company].filter(Boolean).join(' · ') || '(ללא שם)'

  const renderProfileRow = (p: DuplicateProfile, idx: number, total: number) => (
    <div
      key={p._id}
      style={{
        display: 'grid',
        gridTemplateColumns: PROFILE_COLUMNS,
        gap: '12px',
        padding: '10px 16px',
        alignItems: 'center',
        fontSize: '13px',
        color: colors.textPrimary,
        background: colors.bgSecondary,
        borderTop: `1px solid ${colors.borderColor}`,
      }}
    >
      <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
        <img
          src={avatarUrl(p)}
          alt=''
          onError={(e) => {
            const img = e.currentTarget
            const fallback = `https://ui-avatars.com/api/?name=${encodeURIComponent(
              p.name || '?'
            )}&background=random&color=fff&size=40`
            if (img.src !== fallback) img.src = fallback
          }}
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            objectFit: 'cover',
            flexShrink: 0,
            border: `1px solid ${colors.borderColor}`,
          }}
        />
        <span style={{fontWeight: 600}}>{p.name || '(ללא שם)'}</span>
        {/* Newest row is nearly always the one in use; the rest are leftovers. */}
        {idx === total - 1 && <Badge text='החדש ביותר' color={colors.accentSuccess} />}
      </div>
      {/* Owning account, per row: in a cross-account group this is the whole point,
          and it's the fastest way to jump to the customer either way. */}
      <div style={{minWidth: 0}}>
        <span
          role='button'
          title={`${accountLabel(p)}\n${p.account_email || ''}\n(לחצו למעבר לחשבון)`}
          onClick={() => navigate(`/admin/accounts/${p.account_id}`)}
          style={{
            display: 'block',
            fontWeight: 600,
            color: colors.accentPrimary,
            cursor: 'pointer',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {accountLabel(p)}
        </span>
        {p.account_email && (
          <span
            style={{
              display: 'block',
              color: colors.textMuted,
              fontSize: '11px',
              direction: 'ltr',
              textAlign: 'right',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {p.account_email}
          </span>
        )}
      </div>
      <div style={{color: colors.textSecondary, direction: 'ltr', textAlign: 'right'}}>
        {p.facebook_id || '—'}
      </div>
      <div style={{color: colors.textSecondary, direction: 'ltr', textAlign: 'right', wordBreak: 'break-all'}}>
        {p.facebook_username || '—'}
      </div>
      <div style={{color: colors.textMuted}}>{fmtDate(p.created_at)}</div>
      <div style={{display: 'flex', gap: '6px', flexWrap: 'wrap'}}>
        {p.hidden && <Badge text='מוסתר' color={colors.textMuted} />}
        {p.is_logged_in ? (
          <Badge text='מחובר' color={colors.accentSuccess} />
        ) : (
          <Badge text='לא מחובר' color={colors.accentDanger} />
        )}
        {!p.autopost_enabled && <Badge text='פרסום כבוי' color={colors.accentWarning} />}
      </div>
    </div>
  )

  return (
    <div style={{padding: 0}}>
      <div style={{marginBottom: '32px'}}>
        <h1 style={{fontSize: '24px', fontWeight: 600, color: colors.textPrimary, margin: 0}}>
          פרופילים כפולים
        </h1>
        <p style={{color: colors.textSecondary, marginTop: '8px', fontSize: '14px'}}>
          פרופילים ראשיים (לא תתי־פרופילים) עם אותו מזהה פייסבוק שמופיעים יותר מפעם אחת —
          באותו חשבון, או אצל לקוחות שונים (מסומן "מפוצל בין חשבונות"). לצד כל פרופיל מוצג
          החשבון שאליו הוא שייך. מתוך החשבונות שתקופת הניסיון שלהם עדיין פעילה, ורק פרופילים
          אמיתיים (לא פיקטיביים) שהחשבון שלהם בפייסבוק פעיל (לא נעול)
        </p>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
          gap: '16px',
          marginBottom: '24px',
        }}
      >
        {statsData.map((stat, i) => (
          <div key={i} style={{background: colors.bgTertiary, borderRadius: '12px', padding: '20px'}}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: stat.gradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '12px',
              }}
            >
              <i className={`bi ${stat.icon}`} style={{fontSize: '20px', color: '#fff'}}></i>
            </div>
            <div style={{fontSize: '28px', fontWeight: 700, color: colors.textPrimary, marginBottom: '4px'}}>
              {stat.value.toLocaleString()}
            </div>
            <div style={{fontSize: '13px', color: colors.textSecondary}}>{stat.label}</div>
          </div>
        ))}
      </div>

      <div
        style={{
          display: 'flex',
          gap: '16px',
          marginBottom: '20px',
          alignItems: 'center',
          background: colors.bgTertiary,
          padding: '12px 16px',
          borderRadius: '8px',
        }}
      >
        <div style={{flex: 1, position: 'relative'}}>
          <i
            className='bi bi-search'
            style={{
              position: 'absolute',
              right: '14px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: colors.textMuted,
              fontSize: '16px',
            }}
          ></i>
          <input
            type='text'
            placeholder='חיפוש לפי שם חשבון, אימייל, שם פרופיל, מזהה פייסבוק...'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 44px 12px 16px',
              background: 'transparent',
              border: 'none',
              borderRadius: '6px',
              color: colors.textPrimary,
              fontSize: '14px',
              outline: 'none',
            }}
          />
        </div>
        <div style={{fontSize: '13px', color: colors.textSecondary}}>{filtered.length} קבוצות</div>
      </div>

      {loading && (
        <div style={{padding: '40px', textAlign: 'center', color: colors.textSecondary}}>טוען…</div>
      )}

      {!loading && error && (
        <div style={{padding: '20px', borderRadius: '8px', background: 'rgba(246,78,96,0.1)', color: colors.accentDanger}}>
          {error}
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div style={{padding: '40px', textAlign: 'center', color: colors.textSecondary}}>
          לא נמצאו פרופילים כפולים
        </div>
      )}

      <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
        {!loading &&
          !error &&
          filtered.map((g) => (
            <div
              key={`${g.account_id}-${g.match_value}`}
              style={{background: colors.bgTertiary, borderRadius: '12px', overflow: 'hidden'}}
            >
              {/* Group header — account + why these rows were matched */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                  padding: '14px 16px',
                  flexWrap: 'wrap',
                }}
              >
                <div style={{display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap'}}>
                  <Badge text={`${g.count} עותקים`} color={colors.accentDanger} />
                  {g.scope === 'cross_account' ? (
                    <>
                      <Badge
                        text={`מפוצל בין ${g.accounts.length} חשבונות`}
                        color={colors.accentWarning}
                      />
                      <span style={{fontWeight: 600, color: colors.textPrimary, fontSize: '14px'}}>
                        {g.accounts.map((a) => accountLabel(a)).join(' | ')}
                      </span>
                    </>
                  ) : (
                    <>
                      <span style={{fontWeight: 600, color: colors.textPrimary, fontSize: '14px'}}>
                        {accountLabel(g)}
                      </span>
                      <span style={{color: colors.textMuted, fontSize: '12px', direction: 'ltr'}}>
                        {g.account_email}
                      </span>
                    </>
                  )}
                </div>
                <div style={{display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'}}>
                  <Badge text='אותו מזהה פייסבוק' color={colors.accentPrimary} />
                  <span style={{color: colors.textMuted, fontSize: '12px', direction: 'ltr'}}>
                    {g.match_value}
                  </span>
                  {/* One account -> one button. Split groups have no single target:
                      each row's account name is the link instead. */}
                  {g.scope === 'account' && (
                    <button
                      onClick={() => navigate(`/admin/accounts/${g.account_id}`)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '8px',
                        border: 'none',
                        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                        color: '#fff',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '11px',
                      }}
                    >
                      לחשבון
                    </button>
                  )}
                </div>
              </div>

              {/* Column headers */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: PROFILE_COLUMNS,
                  gap: '12px',
                  padding: '8px 16px',
                  fontSize: '11px',
                  fontWeight: 600,
                  color: colors.textMuted,
                  background: colors.bgHover,
                }}
              >
                <div>שם הפרופיל</div>
                <div>חשבון</div>
                <div>מזהה פייסבוק</div>
                <div>שם משתמש</div>
                <div>נוצר</div>
                <div>סטטוס</div>
              </div>

              {g.profiles.map((p, i) => renderProfileRow(p, i, g.profiles.length))}
            </div>
          ))}
      </div>
    </div>
  )
}

export default DuplicateProfilesPage
